import { v4 as uuidv4 } from 'uuid'
import savePdfMonthlyReport from '../../../axios/fiscal/monthlyReport/savePdf'
import { logger } from '../../../../logger/logger'

const TIMEOUT_MS = 30_000

/**
 * Sentinel reason for the 30s frontend timeout. Exported so the consuming hook
 * can identify the case via `error.message === SAVE_PDF_TIMEOUT_REASON` without
 * a magic string at the call site (AI TechLead IMP-2).
 */
export const SAVE_PDF_TIMEOUT_REASON = 'SAVE_PDF_TIMEOUT'

/**
 * Module-level in-flight map keyed by reportId. Layer 2 dedup: 20 concurrent
 * dispatches on the same reportId fire only one POST.
 *
 * **Multi-tab caveat (AI TechLead CRIT-3):** the dedup is per-window-scope,
 * not per-user. If the same user opens the report in two browser tabs and
 * clicks Save PDF in both, each tab has its own Map → two POSTs → two backend
 * renders → each tab gets its own download. This is the intended UX (each tab
 * is an independent session) and matches the existing Print path; the spec's
 * "1 backend render per click" claim refers to the single-tab case.
 */
const inFlightSavePdf = new Map()

// eslint-disable-next-line no-underscore-dangle
export const __resetForTest = () => {
	for (const entry of inFlightSavePdf.values()) {
		clearTimeout(entry.timeoutId)
	}
	inFlightSavePdf.clear()
}

export const saveMonthlyReportPdfThunk = (reportId, locale) => async () => {
	if (inFlightSavePdf.has(reportId)) {
		return inFlightSavePdf.get(reportId).promise
	}

	const traceId = uuidv4()
	let resolveFn
	let rejectFn
	const promise = new Promise((resolve, reject) => {
		resolveFn = resolve
		rejectFn = reject
	})

	const timeoutId = setTimeout(() => {
		logger.info('[SavePdf] timeout', { reportId, traceId, stage: 'timeout' })
		inFlightSavePdf.delete(reportId)
		rejectFn(new Error(SAVE_PDF_TIMEOUT_REASON))
	}, TIMEOUT_MS)

	inFlightSavePdf.set(reportId, {
		promise,
		traceId,
		timeoutId,
		resolve: resolveFn,
		reject: rejectFn,
	})

	// Swallow the unhandled-rejection that Jest/browsers emit if a settle
	// (timeout/FAILED) lands while the consumer hasn't attached a .catch yet
	// (e.g., component unmounted). Real consumers attach a .catch — this is
	// only a safety net to keep the console clean (AI TechLead IMP-1).
	promise.catch(() => {})

	try {
		logger.info('[SavePdf] post-sent', { reportId, traceId, locale, stage: 'post-sent' })
		await savePdfMonthlyReport(reportId, traceId, locale)
	} catch (err) {
		logger.warn('[SavePdf] post-failed', { reportId, traceId, error: err?.message ?? String(err), stage: 'post-failed' })
		clearTimeout(timeoutId)
		inFlightSavePdf.delete(reportId)
		rejectFn(err)
		// Explicit return prevents fall-through; the rejected `promise` is
		// what the caller awaits (AI QA W1).
		return promise
	}

	return promise
}

const findEntryByTraceId = (traceId) => {
	for (const [reportId, entry] of inFlightSavePdf.entries()) {
		if (entry.traceId === traceId) {
			return { reportId, ...entry }
		}
	}
	return null
}

export const handleSavePdfResponse = (traceId, html, filename) => {
	const entry = findEntryByTraceId(traceId)
	if (!entry) {
		return
	}
	clearTimeout(entry.timeoutId)
	inFlightSavePdf.delete(entry.reportId)
	entry.resolve({ html, filename })
}

export const handleSavePdfFailed = (traceId, reason) => {
	const entry = findEntryByTraceId(traceId)
	if (!entry) {
		return
	}
	clearTimeout(entry.timeoutId)
	inFlightSavePdf.delete(entry.reportId)
	entry.reject(new Error(reason))
}
